import { Button } from 'components/ui/button';
import * as Dialog from '@radix-ui/react-dialog';
import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { X } from 'lucide-react';
import { RootState } from 'store';
import SaveImageModal from './SaveImageModal';
import AuthRequiredModal from './AuthRequiredModal';
import { LogoGenService } from 'features/text-to-logo/services/logo-gen.api';
import toast from 'react-hot-toast';
import { useMutation } from '@tanstack/react-query';
import { saveLogoInfo, saveFile, setFileName } from 'store/slices/logoGen';

interface RegenerateModalProps {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export default function RegenerateModal({ isOpen: controlledIsOpen, onOpenChange }: RegenerateModalProps) {
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const dispatch = useDispatch();
  
  // Use controlled state if provided, otherwise use uncontrolled
  const isOpen = controlledIsOpen ?? uncontrolledIsOpen;
  const setIsOpen = onOpenChange ?? setUncontrolledIsOpen;

  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const { 
    companyName, 
    companyDescription, 
    style, 
    industry, 
    tone, 
    color, 
    negativePrompt 
  } = useSelector((state: RootState) => state.logoGen);

  const handleSaveClick = () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      setIsOpen(false); // Close regenerate modal
    } else {
      setShowSaveModal(true);
    }
  };

  const { mutate, isPending } = useMutation({
    mutationKey: ['regenerate-logo'],
    mutationFn: () => LogoGenService.generateLogoFromText({
      company_name: companyName,
      company_description: companyDescription,
      style,
      industry,
      tone,
      color,
      negative_prompt: negativePrompt
    }),
    onSuccess: (data) => {
      // Update Redux state with new logo URL
      dispatch(saveLogoInfo({
        tempLogoUrl: data.data.logo,
        style,
        companyName,
        companyDescription,
        industry,
        tone,
        color,
        negativePrompt,
      }));
      
      // Reset file saved status
      dispatch(saveFile({ fileSaved: false }));
      
      // Update file name
      dispatch(setFileName({ fileName: 'Untitled 1' }));
      
      // Close modal
      setIsOpen(false);
      
      toast.success('Logo regenerated successfully!', {
        position: 'bottom-center',
      });
    },
    onError: () => {
      toast.error('Failed to regenerate logo. Please try again.', {
        position: 'bottom-center',
      });
    },
  });

  return (
    <>
      <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
        {!controlledIsOpen ? (
          // Desktop trigger
          <Dialog.Trigger asChild>
            <button
              type="button"
              className="lg:text-left lg:text-primaryAccent-9 mt-2 gap-2 w-full lg:underline font-medium text-sm lg:mt-0"
            >
              Regenerate logo
            </button>
          </Dialog.Trigger>
        ) : null}
        
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-50" />
          <Dialog.Content className="fixed top-1/2 left-1/2 w-[90%] max-w-[480px] -translate-x-1/2 -translate-y-1/2 bg-background text-text-primary p-6 rounded-lg shadow-lg">
            <Dialog.Close className="absolute right-2 top-2 text-gray-400 hover:text-gray-600">
              <X className="h-4 w-4" />
            </Dialog.Close>
            
            <Dialog.Title className="text-xl font-medium">
              Regenerate logo?
            </Dialog.Title>
            <p className="mt-2 text-sm">
              Are you sure you want to regenerate this logo? Please save the
              current version to ensure that you don&apos;t lose this logo.
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <Button
                onClick={() => mutate()}
                variant={'customPrimary'}
                size={'sm'}
                type="button"
                disabled={isPending}
                className="px-4 py-2 text-sm"
              >
                {isPending ? 'Regenerating...' : 'Continue without saving'}
              </Button>
              <Button
                onClick={handleSaveClick}
                variant={'customSecondary'}
                size={'sm'}
                type="button"
                className="px-4 py-2 text-sm"
              >
                Save
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Auth Required Modal */}
      <AuthRequiredModal
        isOpen={showAuthModal}
        onOpenChange={setShowAuthModal}
        type="save"
      />

      {/* Save Image Modal - Only shown when authenticated */}
      {isAuthenticated && (
        <SaveImageModal
          isOpen={showSaveModal}
          onOpenChange={setShowSaveModal}
        />
      )}
    </>
  );
}